import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer
} from 'recharts';

import { useCallData } from '../../contexts/CallDataContext';
import { useWeek } from '../../contexts/WeekContext';
import { format, addDays, isWithinInterval, startOfDay, endOfDay, differenceInDays } from 'date-fns';

import { parseCustomTimestamp } from '../../utils/dateUtils';
import { getCallDurationInSeconds } from '../../api/callData';

const DurationChart = () => {
    const { data } = useCallData();
    const { dateRange } = useWeek(); // Changed from selectedWeek to dateRange

    // Process data to get average duration per day for the selected date range
    // This function will now return both the chart data and the average for the period
    const processDataForChart = () => {
        if (!dateRange || !dateRange.start || !dateRange.end) {
            return { chartData: [], weeklyAverageMinutes: '0.0' };
        }

        // Calculate the number of days in the selected range
        const dayCount = differenceInDays(dateRange.end, dateRange.start) + 1;
        
        // Generate dates for the selected range
        const rangeDates = Array.from({ length: dayCount }, (_, i) => 
            addDays(dateRange.start, i)
        );
        
        const dailyData = rangeDates.reduce((acc, date) => {
            acc[format(date, 'MM/dd')] = {
                totalSeconds: 0,
                callCount: 0
            };
            return acc;
        }, {});

        let totalSeconds = 0;
        let totalCalls = 0;

        // Sum up durations for each day in the selected range
        data.forEach(call => {
            const callDate = parseCustomTimestamp(call?.metadata?.timestamp);
            if (callDate) {
                const rangeStart = startOfDay(dateRange.start);
                const rangeEnd = endOfDay(dateRange.end);

                if (isWithinInterval(callDate, { start: rangeStart, end: rangeEnd })) {
                    const durationInSeconds = getCallDurationInSeconds(call);
                    
                    // Add to daily totals
                    const formattedDate = format(callDate, 'MM/dd', { timeZone: 'UTC' });
                    if (dailyData.hasOwnProperty(formattedDate)) {
                        dailyData[formattedDate].totalSeconds += durationInSeconds;
                        dailyData[formattedDate].callCount++;
                    }

                    // Add to period totals
                    totalSeconds += durationInSeconds;
                    totalCalls++;
                }
            }
        });

        // Calculate average duration PER DAY for the chart
        const chartData = rangeDates.map(date => {
            const dayKey = format(date, 'MM/dd');
            const dayInfo = dailyData[dayKey];
            const dailyAverageSeconds = dayInfo.callCount > 0 ? dayInfo.totalSeconds / dayInfo.callCount : 0;
            
            return {
                date: dayKey,
                // Convert daily average seconds to minutes
                duration: +(dailyAverageSeconds / 60).toFixed(1)
            };
        });

        // Calculate average duration PER CALL for the period
        const averageSeconds = totalCalls > 0 ? totalSeconds / totalCalls : 0;
        const averageMinutes = (averageSeconds / 60).toFixed(1);

        return { chartData, weeklyAverageMinutes: averageMinutes };
    };

    const { chartData, weeklyAverageMinutes } = processDataForChart();

    return (
        <div className="flex flex-col items-center card-container w-full h-[300px] space-y-2">
            <div className='flex flex-row w-full items-center justify-between'>
                <div className="text-sm text-font-primary mb-2">Average Call Duration</div>
                <span className='text-lg font-semibold'>{weeklyAverageMinutes} m</span>
            </div>
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -30, bottom: -5 }}>
                    <XAxis dataKey="date" stroke="#aaa" />
                    <YAxis stroke="#aaa" />
                    <Tooltip />
                    <Area
                        type="monotone"
                        dataKey="duration"
                        stroke="#F29E4C"
                        strokeWidth="3"
                        fill="#F29E4C"
                        fillOpacity={0.2}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

export default DurationChart;
  
